(() => {
  if (window.fwdNewsletterController) {
    window.fwdNewsletterController.refresh(document);
    return;
  }

  let toastTimer;

  const showToast = (message, { error = false } = {}) => {
    let toast = document.querySelector('[data-fwd-newsletter-toast]');

    if (!toast) {
      toast = document.createElement('p');
      toast.className = 'fwd-toast';
      toast.dataset.fwdNewsletterToast = '';
      document.body.appendChild(toast);
    }

    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toast.classList.toggle('is-error', error);
    toast.classList.add('is-visible');
    toast.setAttribute('role', error ? 'alert' : 'status');
    toast.setAttribute('aria-live', error ? 'assertive' : 'polite');
    toast.setAttribute('aria-atomic', 'true');

    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.hidden = true;
    }, 4200);
  };

  const initializeNewsletter = (root) => {
    if (root.dataset.fwdNewsletterInitialized === 'true') return;
    const form = root.querySelector('form');
    if (!form) return;
    const email = form.querySelector('input[type="email"]');
    const submit = form.querySelector('button[type="submit"]');
    const feedback = form.querySelector('[data-fwd-newsletter-feedback]');
    if (!email || !submit || !feedback) return;
    root.dataset.fwdNewsletterInitialized = 'true';

    let submitting = false;
    let hasInteracted = false;
    const defaultSubmitLabel = submit.textContent.trim();
    const loadingSubmitLabel = submit.dataset.loadingLabel || 'SUBSCRIBING...';
    const defaultFeedback = feedback.dataset.defaultMessage || feedback.textContent.trim();
    const analyticsSurface = root.closest?.('[data-fwd-newsletter-dialog]') ? 'newsletter_dialog' : 'footer';
    const trackNewsletterCompletion = (result) => {
      try {
        window.fwdAnalytics?.track?.('Newsletter Signup Completed', {
          surface: analyticsSurface,
          result,
        });
      } catch (_error) {
        // Newsletter completion must never depend on analytics.
      }
    };

    const setFeedback = (message, { error = false } = {}) => {
      feedback.textContent = message;
      feedback.classList.toggle('is-error', error);
      feedback.setAttribute('role', error ? 'alert' : 'status');
      feedback.setAttribute('aria-live', error ? 'assertive' : 'polite');
      email.setAttribute('aria-invalid', String(error));
    };

    const hasCompleteEmailDomain = () => {
      const value = email.value.trim();
      const atIndex = value.lastIndexOf('@');
      if (atIndex <= 0 || atIndex !== value.indexOf('@')) return false;

      const domain = value.slice(atIndex + 1);
      if (domain.length > 253) return false;

      const labels = domain.split('.');
      if (labels.length < 2) return false;

      const validLabel = /^[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?$/u;
      if (labels.some((label) => !validLabel.test(label))) return false;

      const topLevelDomain = labels[labels.length - 1];
      if (/^xn--[a-z0-9-]{2,}$/i.test(topLevelDomain)) return true;
      return Array.from(topLevelDomain).length >= 2 && /^\p{L}+$/u.test(topLevelDomain);
    };

    const isEmailValid = () => email.validity.valid && hasCompleteEmailDomain();

    const getClientError = () => {
      if (email.value.trim() === '') return '이메일 주소를 입력해 주세요.';
      if (email.validity.typeMismatch || !isEmailValid()) {
        return '올바른 이메일 형식으로 입력해 주세요.';
      }
      return '';
    };

    const showClientValidation = () => {
      const message = getClientError();
      setFeedback(message || defaultFeedback, { error: Boolean(message) });
      return message === '';
    };

    const getServerError = (message = '') => {
      const normalized = message.toLocaleLowerCase();
      if (/already|taken|registered|exists|이미|등록/.test(normalized)) {
        return '이미 뉴스레터에 가입된 이메일입니다.';
      }
      if (/invalid|format|올바르지|유효하지|형식/.test(normalized)) {
        return '올바른 이메일 형식으로 입력해 주세요.';
      }
      return message || '가입을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
    };

    const updateState = () => {
      const enabled = !submitting && isEmailValid();
      submit.disabled = !enabled;
      submit.setAttribute('aria-disabled', String(!enabled));
    };

    email.addEventListener('input', () => {
      updateState();
      if (isEmailValid()) setFeedback(defaultFeedback);
      else if (hasInteracted) showClientValidation();
    });
    email.addEventListener('blur', () => {
      hasInteracted = true;
      showClientValidation();
    });
    email.addEventListener('change', () => {
      hasInteracted = true;
      updateState();
      showClientValidation();
    });
    form.addEventListener('submit', async (event) => {
      hasInteracted = true;
      if (!showClientValidation()) {
        event.preventDefault();
        email.reportValidity();
        updateState();
        return;
      }

      if (!window.fetch || !window.FormData || !window.DOMParser) return;

      event.preventDefault();
      submitting = true;
      submit.textContent = loadingSubmitLabel;
      form.setAttribute('aria-busy', 'true');
      setFeedback('가입을 처리하고 있습니다.');
      updateState();

      try {
        const response = await window.fetch(form.action, {
          method: form.method || 'post',
          body: new window.FormData(form),
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const responseHtml = await response.text();
        const responseDocument = new window.DOMParser().parseFromString(responseHtml, 'text/html');
        const responseForm = responseDocument.querySelector(`#${form.id}`);
        const responseUrl = new URL(response.url || form.action, window.location.href);
        const succeeded = Boolean(
          responseForm?.querySelector('[data-fwd-newsletter-success]')
          || responseUrl.searchParams.get('customer_posted') === 'true'
        );

        if (!response.ok || !succeeded) {
          const serverMessage = responseForm
            ?.querySelector('[data-fwd-newsletter-error]')
            ?.textContent
            ?.trim();
          const signupError = new Error('Newsletter signup failed');
          signupError.userMessage = getServerError(serverMessage);
          throw signupError;
        }

        form.reset();
        hasInteracted = false;
        setFeedback(defaultFeedback);
        showToast('가입해 주셔서 감사합니다.');
        trackNewsletterCompletion('success');
      } catch (error) {
        setFeedback(getServerError(error.userMessage), { error: true });
        trackNewsletterCompletion('failure');
      } finally {
        submitting = false;
        submit.textContent = defaultSubmitLabel;
        form.removeAttribute('aria-busy');
        updateState();
      }
    });
    updateState();
  };

  let newsletterDialogReturnFocus = null;

  const isUsableReturnFocus = (target) => Boolean(
    target?.isConnected
    && !target.disabled
    && !target.closest?.('[inert]')
    && target.getClientRects?.().length
  );

  const getReturnFocusTarget = (trigger) => {
    const drawer = trigger.closest?.('header-drawer');
    if (drawer) return drawer.querySelector?.(':scope > details > summary') || trigger;

    const parentItem = trigger.closest?.('.fwd-nav__item--has-children');
    const parentToggle = parentItem?.querySelector?.(':scope > .fwd-nav__parent-row .fwd-nav__toggle');
    return parentToggle || trigger;
  };

  const focusDialogReturnTarget = () => {
    const preferredTarget = newsletterDialogReturnFocus;
    const findFallback = () => [...document.querySelectorAll(
      'header-drawer > details > summary, .fwd-nav__item--has-children > .fwd-nav__parent-row .fwd-nav__toggle, [data-newsletter-trigger]'
    )].find(isUsableReturnFocus);
    const focusTarget = isUsableReturnFocus(preferredTarget) ? preferredTarget : findFallback();

    if (focusTarget) {
      focusTarget.focus({ preventScroll: true });
      newsletterDialogReturnFocus = null;
      return;
    }

    window.requestAnimationFrame(() => {
      const delayedTarget = isUsableReturnFocus(preferredTarget) ? preferredTarget : findFallback();
      delayedTarget?.focus({ preventScroll: true });
      newsletterDialogReturnFocus = null;
    });
  };

  const setDialogScrollLock = (locked) => {
    document.documentElement?.classList?.toggle('fwd-newsletter-dialog-open', locked);
    document.body?.classList?.toggle('fwd-newsletter-dialog-open', locked);
  };

  const closeNavigation = (trigger, event) => {
    const navigation = trigger.closest?.('fwd-navigation');
    navigation?.closeAll?.();

    const drawer = trigger.closest?.('header-drawer');
    const drawerSummary = drawer?.querySelector?.(':scope > details > summary');
    if (drawer?.closeMenuDrawer && drawerSummary) {
      drawer.closeMenuDrawer(event, drawerSummary);
    }
  };

  const initializeTrigger = (trigger) => {
    if (trigger.dataset.newsletterTriggerInitialized === 'true') return;
    trigger.dataset.newsletterTriggerInitialized = 'true';
    trigger.addEventListener('click', (event) => {
      const newsletterDialog = document.querySelector('[data-fwd-newsletter-dialog]');
      if (!newsletterDialog || typeof newsletterDialog.showModal !== 'function') return;

      event.preventDefault();
      newsletterDialogReturnFocus = getReturnFocusTarget(trigger);
      closeNavigation(trigger, event);
      if (!newsletterDialog.open) newsletterDialog.showModal();
      setDialogScrollLock(true);
      const newsletterDialogInput = newsletterDialog.querySelector('input[type="email"]');
      window.requestAnimationFrame(() => newsletterDialogInput?.focus());
    });
  };

  const initializeDialog = (newsletterDialog) => {
    if (newsletterDialog.dataset.fwdNewsletterDialogInitialized === 'true') return;
    newsletterDialog.dataset.fwdNewsletterDialogInitialized = 'true';
    const newsletterDialogClose = newsletterDialog.querySelector('[data-fwd-newsletter-dialog-close]');

    newsletterDialogClose?.addEventListener('click', () => newsletterDialog.close());
    newsletterDialog.addEventListener('click', (event) => {
      if (event.target === newsletterDialog) newsletterDialog.close();
    });
    newsletterDialog.addEventListener('close', () => {
      setDialogScrollLock(false);
      focusDialogReturnTarget();
    });
  };

  const findWithin = (scope, selector) => {
    const matches = scope.matches?.(selector) ? [scope] : [];
    return [...matches, ...scope.querySelectorAll(selector)];
  };

  const refresh = (scope = document) => {
    findWithin(scope, '[data-fwd-newsletter-form]').forEach(initializeNewsletter);
    findWithin(scope, '[data-newsletter-trigger]').forEach(initializeTrigger);
    findWithin(scope, '[data-fwd-newsletter-dialog]').forEach(initializeDialog);

    const hasOpenDialog = Boolean(document.querySelector('[data-fwd-newsletter-dialog][open]'));
    setDialogScrollLock(hasOpenDialog);
  };

  window.fwdNewsletterController = { refresh };
  refresh(document);
  document.addEventListener?.('shopify:section:load', (event) => refresh(event.target));

  const successToast = document.querySelector('[data-fwd-newsletter-success]');
  if (successToast) {
    showToast(successToast.textContent.trim());
  }
})();
